import { Dispatch } from 'redux';
import { ENTITIES } from 'src/constants/entityConsts';
import fileHandler from 'src/websocket/FileHandler';
import channelHandler from 'src/websocket/ChannelHandler';
import invoiceHandler from 'src/websocket/InvoiceHandler';
import paymentHandler from 'src/websocket/paymentHandler';
import InternalUserHandler from 'src/websocket/InternalUserHandler';
import clientHandler from 'src/websocket/ClientHandler';
import companyHandler from 'src/websocket/CompanyHandler';
import { FileNotificationsHandler } from 'src/websocket/NotificationsHandler';
import { NotificationGroups } from 'src/store/notifications/types';
import { FlagContextType, PortalConfig } from 'src/context';
import FormRequestHandler from 'src/websocket/FormRequestHandler';
import formRequestNotificationHandler from 'src/websocket/FormRequestNotificationHandler';
import formCompletedNotificationHandler from 'src/websocket/FormCompletedNotificationHandler';
import FormTemplatesHandler from 'src/websocket/FormTemplateHandler';
import subscriptionHandler from 'src/websocket/SubscriptionHandler';
import newInvoiceNotificationHandler from 'src/websocket/NewInvoiceNotificationHandler';
import PortalSettingsHandler from 'src/websocket/PortalSettingsHandler';
import CustomFieldsHandler from 'src/websocket/CustomFieldsHandler';
import { MessageSettingsHandler } from 'src/websocket/MessageSettingsHandler';
import ExtensionItemHandler from 'src/websocket/ExtensionItemHandler';
import TagHandler from 'src/websocket/TagHandler';
import ArticleHandler from 'src/websocket/ArticleHandler';
import KnowledgeBaseSettingsHandler from 'src/websocket/KnowledgeBaseSettingsHandler';
import { WebSocketMutations } from 'src/hooks/useWebSocketMutations';
import { insertInboxNotifications } from 'src/websocket/inboxNotificationHandler';
import { FormsV2ResponseHandler } from 'src/websocket/FormsV2ResponseHandler';
import FormsTemplatesV2Handler from 'src/websocket/formsTemplatesV2Handler';
import { OnboardingTaskStatusHandler } from 'src/websocket/OnboardingTaskStatusHandler';
import { handleContractTemplateUpdate } from './contractTemplateHandler';
import { Events } from 'src/constants/webSocketConsts/events';

export interface MsgEvent {
  entity: string;
  event: Events;
  items: Array<any>;
}

export const wsMessageHandler = (
  msgEvent: MsgEvent,
  portalConfig: PortalConfig,
  userID: string,
  webSocketMutations: WebSocketMutations,
  flags: FlagContextType,
  dispatch: Dispatch<any>,
) => {
  const { entity, event, items } = msgEvent;
  // forms response ws item entity string
  // is a combination of the entity name and the formResponseId
  // so we need to check if the entity string includes the form response
  // entity name.
  if (entity.includes(ENTITIES.FORM_RESPONSE)) {
    return FormsV2ResponseHandler(event, items, dispatch);
  }
  switch (entity) {
    case ENTITIES.FILE_METADATA:
      return fileHandler(event, items);
    case ENTITIES.STRIPE_INVOICE:
      return invoiceHandler(event, items);
    case ENTITIES.STRIPE_SUBSCRIPTION:
      return subscriptionHandler(event, items);
    case ENTITIES.FILE_CHANNEL:
      return channelHandler(event, items);
    case ENTITIES.CLIENT_USER:
      return clientHandler(event, items, portalConfig, userID);
    case ENTITIES.INTERNAL_USER:
      return InternalUserHandler(event, items, portalConfig, userID);
    case ENTITIES.CLIENT_COMPANY:
      return companyHandler(event, items);
    case ENTITIES.PAYMENT_INFO:
      return paymentHandler(event, items);
    case ENTITIES.INTAKE_FORM_RESPONSE:
      return FormRequestHandler(event, items);
    case ENTITIES.NOTIFY_NEW_INVOICE:
      return newInvoiceNotificationHandler(event, items);
    case ENTITIES.NOTIFY_FILE_UPLOADED:
      return FileNotificationsHandler(
        NotificationGroups.FileNotifications,
        event,
        items,
        // allow marking notification as read only when
        // a. user is a client
        // b. files notification center flag in off
        portalConfig.viewMode === 'client' ||
          !flags.EnableFilesNotificationCenter,
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST:
      return FileNotificationsHandler(
        NotificationGroups.EsigRequestNotifications,
        event,
        items,
      );
    case ENTITIES.NOTIFY_FILE_ESIGN_REQUEST_COMPLETE:
      FileNotificationsHandler(
        NotificationGroups.EsigRequestCompleteNotifications,
        event,
        items,
      );
      return insertInboxNotifications(items);
    case ENTITIES.NOTIFY_FORM_REQUEST:
      return formRequestNotificationHandler(event, items);
    case ENTITIES.NOTIFY_FORM_COMPLETE:
      return formCompletedNotificationHandler(event, items);
    case ENTITIES.INTAKE_FORM_TEMPLATE:
      return FormTemplatesHandler(event, items);
    case ENTITIES.FORM_TEMPLATE:
      return FormsTemplatesV2Handler(event, items);
    case ENTITIES.DOMAIN_SETTINGS:
      // invalidate cache for recommended resources on domain connected event, so that refreshed data is fetched when user user go to home page
      webSocketMutations.invalidateRecommendedResources();
      webSocketMutations.setVerifiedDomains(items);
      break;
    case ENTITIES.REFERRAL:
      // update the cache for referral
      webSocketMutations.invalidateReferrals();
      break;
    case ENTITIES.PORTAL_SETTINGS:
      return PortalSettingsHandler(event, items);
    case ENTITIES.MESSAGE_SETTINGS:
      return MessageSettingsHandler(event, items);
    case ENTITIES.CLIENT_CUSTOM_FIELDS:
      return CustomFieldsHandler(event, items);
    case ENTITIES.EXTENSION_ITEM:
      return ExtensionItemHandler(event, items);
    case ENTITIES.TAG:
      return TagHandler(event, items);
    case ENTITIES.ARTICLE:
      return ArticleHandler(event, items);
    case ENTITIES.KNOWLEDGE_BASE_SETTINGS:
      return KnowledgeBaseSettingsHandler(event, items);
    case ENTITIES.PORTAL_CONFIG:
      if (portalConfig.updatePortalConfig) {
        // portal config by default does not have update method
        // this gets initialized in App.tsx and passed to websocket handler
        portalConfig.updatePortalConfig(items[0].fields);
      }
      break;
    case ENTITIES.NOTIFICATION_GROUPING:
      return insertInboxNotifications(items);
    case ENTITIES.ONBOARDING_TASK_STATUS:
      return OnboardingTaskStatusHandler(items[0]);
    case ENTITIES.CONTRACT_TEMPLATE:
      return handleContractTemplateUpdate(event, items, dispatch);
    default:
      break;
  }
  return null;
};
